import type { FormScope } from '@rvf/react-router'
import { useControlField, useFormScopeOrContext } from '@rvf/react-router'
import { LucideFiles, LucideLayoutList, LucideTable2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { CheckboxField, FieldContainer } from '~/components/fields'
import { Input, Textarea, ToggleGroup, ToggleGroupItem } from '~/components/ui'
import { useDebounce } from '~/hooks/misc'
import { cn } from '~/lib'
import { type ReportPdfOptions } from '~/models'
import { InfoToolTip } from '../common'

function PdfPreparedForField({
  scope,
  name,
  formContext,
  initialValue,
}: {
  scope: FormScope<ReportPdfOptions>
  name: string
  initialValue?: string
  formContext?: any
}) {
  const [preparedForValue, setPreparedForValue] = useState<string>(
    initialValue || ''
  )
  const debouncedPreparedForValue = useDebounce(preparedForValue, 1000)
  const [__, setPreparedFor] = useControlField<string>(name, scope)

  useEffect(() => {
    setPreparedFor(debouncedPreparedForValue)
  }, [debouncedPreparedForValue, setPreparedFor])

  return (
    <FieldContainer
      label="Recipient Name"
      id="preparedFor"
      className="col-span-12">
      <Input
        id="preparedFor"
        disabled={formContext?.isSubmitting}
        name="preparedFor"
        value={preparedForValue}
        onChange={(e) => setPreparedForValue(e.target.value)}
      />
    </FieldContainer>
  )
}

function PdfNotesField({
  scope,
  name,
  initialValue,
  formContext,
}: {
  scope: FormScope<ReportPdfOptions>
  name: string
  initialValue?: string
  formContext?: any
}) {
  const [notesValue, setNotesValue] = useState<string>(initialValue || '')
  const debouncedNotesValue = useDebounce(notesValue, 1000)
  const [notes, setNotes] = useControlField<string>(name, scope)

  useEffect(() => {
    if (debouncedNotesValue.trim().length === 0) {
      setNotes('')
    } else {
      const lines = debouncedNotesValue.split('\n')
      const html = lines
        .filter((v) => v)
        .map((line) => `<p>${line}</p>`)
        .join('')
      setNotes(html)
    }
  }, [debouncedNotesValue, setNotes])

  return (
    <FieldContainer label="Notes" id="notes" className="col-span-12">
      <input type="hidden" name="notes" value={notes} />
      <Textarea
        id="notes"
        rows={10}
        value={notesValue}
        disabled={formContext?.isSubmitting}
        onChange={(e) => setNotesValue(e.target.value)}
      />
    </FieldContainer>
  )
}

export default function PdfFormContent({
  rootScope,
  pdfOptions,
  singleListing = false,
}: {
  rootScope: FormScope<Record<string, any>>
  pdfOptions?: ReportPdfOptions
  singleListing?: boolean
}) {
  const scope = rootScope.scope('options')
  const formContext = useFormScopeOrContext(rootScope)
  const [type, setType] = useControlField<string>('options.type', rootScope)

  const [showTitlePage] = useControlField<boolean>(
    'options.showTitlePage',
    rootScope
  )

  return (
    <>
      <p className="text-sm italic">
        Note: Form changes in preview may take longer to refresh depending on
        the number of listings
      </p>
      <div className={cn('col-span-12', singleListing && 'hidden')}>
        <p className="mb-3">
          <span className="font-medium">Report Type</span>
          <InfoToolTip className="ml-2">
            <span className="font-semibold">Flyer PDF</span>
            <p className="mb-2">
              One listing per page with options to include additional elements
            </p>
            <span className="font-semibold">Summary PDF</span>
            <p className="mb-2">
              Multiple listings per page with only listing attributes
            </p>
            <span className="font-semibold">Comparison PDF</span>
            <p>Compare four listings side by side per page</p>
          </InfoToolTip>
        </p>
        <input type="hidden" name="type" value={type} />
        <ToggleGroup
          type="single"
          className="justify-start"
          value={type}
          onValueChange={(type) => {
            if (type) setType(type)
          }}
          disabled={formContext?.formState.isSubmitting}>
          <ToggleGroupItem
            value="flyers"
            className="flex text-center text-foreground">
            <LucideFiles className="h-4 w-4" />
            <p className="px-2 text-foreground">Flyers</p>
          </ToggleGroupItem>
          <ToggleGroupItem value="summary">
            <LucideLayoutList className="h-4 w-4" />
            <p className="px-2 text-foreground">Summary</p>
          </ToggleGroupItem>
          <ToggleGroupItem value="comparison">
            <LucideTable2 className="h-4 w-4" />
            <p className="px-2 text-foreground">Comparison</p>
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
      <p className="-mb-2 mt-1 font-medium">
        Title Page
        <InfoToolTip className="ml-2">
          <p>
            Introductory page to PDF Report with space for recipient, notes and
            table of contents
          </p>
        </InfoToolTip>
      </p>
      <CheckboxField
        className="col-span-12"
        label="Show Title Page"
        name="options.showTitlePage"
        disabled={formContext?.formState.isSubmitting}
        scope={rootScope}
      />
      {showTitlePage && (
        <>
          <CheckboxField
            className="col-span-12"
            label="Show Economic Development Contact"
            name="options.showEcDevContact"
            disabled={formContext?.formState.isSubmitting}
            scope={rootScope}
          />
          <CheckboxField
            className={cn('col-span-12', singleListing && 'hidden')}
            label="Show Table of Contents"
            name="options.showContents"
            disabled={formContext?.formState.isSubmitting}
            scope={rootScope}
          />
          <PdfPreparedForField
            scope={scope}
            name="options.preparedFor"
            formContext={formContext}
            initialValue={pdfOptions?.preparedFor}
          />
          <PdfNotesField
            scope={scope}
            name="options.notes"
            formContext={formContext}
            initialValue={
              pdfOptions?.notes
                ? (pdfOptions?.notes || '')
                    .split('</p>')
                    .map((v) => v.replace('<p>', ''))
                    .join('\n\n')
                    .trim()
                : ''
            }
          />
        </>
      )}
      <p className="-mb-2 mt-1 font-medium">
        Map Page
        <InfoToolTip className="ml-2">
          <p>Include numbered listing markers of all listings included</p>
        </InfoToolTip>
      </p>
      <CheckboxField
        className="col-start-1"
        label="Show Map Page"
        name="options.showMapPage"
        disabled={formContext?.formState.isSubmitting}
        scope={rootScope}
      />
      {type === 'flyers' && (
        <>
          <p className="-mb-2 mt-1 font-medium">
            Listings
            <InfoToolTip className="ml-2">
              <p>
                Optionally show listing brokers and include all listings images
                on a separate page
              </p>
            </InfoToolTip>
          </p>
          <CheckboxField
            className="col-span-12"
            label="Show Brokers on Listings"
            name="options.showBrokers"
            disabled={formContext?.formState.isSubmitting}
            scope={rootScope}
          />
          <CheckboxField
            className="col-span-12"
            label="Show Additional Listing Images"
            name="options.showImages"
            disabled={formContext?.formState.isSubmitting}
            scope={rootScope}
          />
        </>
      )}
    </>
  )
}
